/**
 * Fund ledger data hook (NOS-168). Reads ride the fund_ledger_entries RLS
 * (sponsor via is_sponsor_of, child via own membership); writes go through
 * the record_fund_entry RPC.
 */
import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { errorMessage } from "@/lib/errorMessage"
import type { FundLedgerEntry } from "@/lib/fundLedger"

// v1 cap — a fund with more entries needs server-side pagination; the CSV
// export notes truncation when hit.
const ENTRIES_FETCH_CAP = 2000

export interface FundLedger {
  loading: boolean
  entries: FundLedgerEntry[]
  /** From get_fund_balance (authoritative, RLS-scoped). */
  balanceCents: number
  truncated: boolean
  error: string | null
  refresh: () => void
  recordEntry: (input: {
    type: "disbursement" | "adjustment"
    amountCents: number
    memo: string
  }) => Promise<{ error: Error | null }>
}

export function useFundLedger(sponsorshipId: string | null | undefined): FundLedger {
  const [loading, setLoading] = useState(true)
  const [entries, setEntries] = useState<FundLedgerEntry[]>([])
  const [balanceCents, setBalanceCents] = useState(0)
  const [truncated, setTruncated] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchLedger = useCallback(async () => {
    if (!sponsorshipId) {
      setEntries([])
      setBalanceCents(0)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [entriesRes, balanceRes] = await Promise.all([
        supabase
          .from("fund_ledger_entries")
          .select("*")
          .eq("sponsorship_id", sponsorshipId)
          .order("created_at", { ascending: true })
          .limit(ENTRIES_FETCH_CAP),
        supabase.rpc("get_fund_balance", { p_sponsorship_id: sponsorshipId }),
      ])
      if (entriesRes.error) throw entriesRes.error
      if (balanceRes.error) throw balanceRes.error
      const rows = (entriesRes.data ?? []) as FundLedgerEntry[]
      setEntries(rows)
      setTruncated(rows.length === ENTRIES_FETCH_CAP)
      setBalanceCents(Number(balanceRes.data ?? 0))
    } catch (err) {
      console.error("Fund ledger fetch error:", err)
      setError(errorMessage(err, "Failed to load fund ledger"))
    } finally {
      setLoading(false)
    }
  }, [sponsorshipId])

  useEffect(() => {
    fetchLedger()
  }, [fetchLedger])

  const recordEntry = useCallback(
    async ({
      type,
      amountCents,
      memo,
    }: {
      type: "disbursement" | "adjustment"
      amountCents: number
      memo: string
    }) => {
      if (!sponsorshipId) return { error: new Error("No sponsorship") }
      const { error: rpcError } = await supabase.rpc("record_fund_entry", {
        p_sponsorship_id: sponsorshipId,
        p_type: type,
        p_amount_cents: amountCents,
        p_memo: memo,
      })
      if (rpcError) return { error: new Error(rpcError.message) }
      await fetchLedger()
      return { error: null }
    },
    [sponsorshipId, fetchLedger]
  )

  return { loading, entries, balanceCents, truncated, error, refresh: fetchLedger, recordEntry }
}

export interface PortfolioFunds {
  loading: boolean
  /** All ledger entries across the given children (capped). */
  entriesByChild: Map<string, FundLedgerEntry[]>
  truncated: boolean
}

const PORTFOLIO_ENTRIES_CAP = 10000

/** All children's ledger entries for the dashboard column + fund exports. */
export function usePortfolioFunds(childOrgIds: string[]): PortfolioFunds {
  const [loading, setLoading] = useState(true)
  const [entriesByChild, setEntriesByChild] = useState<Map<string, FundLedgerEntry[]>>(new Map())
  const [truncated, setTruncated] = useState(false)
  const idsKey = childOrgIds.slice().sort().join(",")

  useEffect(() => {
    let cancelled = false
    async function fetchAll() {
      if (!idsKey) {
        setEntriesByChild(new Map())
        setLoading(false)
        return
      }
      setLoading(true)
      const { data, error } = await supabase
        .from("fund_ledger_entries")
        .select("*")
        .in("child_org_id", idsKey.split(","))
        .order("created_at", { ascending: true })
        .limit(PORTFOLIO_ENTRIES_CAP)
      if (cancelled) return
      if (error) {
        console.error("Portfolio funds fetch error:", error)
        setEntriesByChild(new Map())
      } else {
        const rows = (data ?? []) as FundLedgerEntry[]
        const map = new Map<string, FundLedgerEntry[]>()
        for (const row of rows) {
          const list = map.get(row.child_org_id) ?? []
          list.push(row)
          map.set(row.child_org_id, list)
        }
        setEntriesByChild(map)
        setTruncated(rows.length === PORTFOLIO_ENTRIES_CAP)
      }
      setLoading(false)
    }
    fetchAll()
    return () => {
      cancelled = true
    }
  }, [idsKey])

  return { loading, entriesByChild, truncated }
}
